import { api } from '../../../shared/lib/axios';
import type { NotaFiscal, NFStatus } from '../../../shared/types';

export const NOTAS_FISCAIS_KEYS = {
  all: ['notas-fiscais'] as const,
  list: (params?: object) => ['notas-fiscais', 'list', params] as const,
  releasableCollaborators: (search?: string) =>
    ['notas-fiscais', 'releasable-collaborators', search ?? ''] as const,
  preview: (collaboratorId?: string, referenceMonth?: string) =>
    ['notas-fiscais', 'release-preview', collaboratorId, referenceMonth] as const,
};

export interface ListParams {
  companyId?: string;
  collaboratorId?: string;
  status?: NFStatus;
  referenceMonth?: string;
  limit?: number;
  offset?: number;
}

interface ListResponse {
  data: NotaFiscal[];
  pagination?: { limit: number; offset: number; count: number };
}

export interface ReleasableCollaborator {
  id: string;
  name: string;
  email: string;
  companyId: string;
}

export interface ReleasePreview {
  collaboratorId: string;
  referenceMonth: string;
  /** Salário vigente na competência; `null` quando não há salário cadastrado. */
  value: number | null;
  alreadyReleased: boolean;
}

export async function listarNotasFiscais(params?: ListParams): Promise<ListResponse> {
  const query = new URLSearchParams();
  if (params?.companyId) query.set('company_id', params.companyId);
  if (params?.collaboratorId) query.set('collaborator_id', params.collaboratorId);
  if (params?.status) query.set('status', params.status);
  if (params?.referenceMonth) query.set('reference_month', params.referenceMonth);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  const { data } = await api.get<ListResponse>(`/notas-fiscais${qs ? `?${qs}` : ''}`);
  return data;
}

export async function listarColaboradoresLiberaveis(search?: string): Promise<ReleasableCollaborator[]> {
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  const { data } = await api.get<{ data: ReleasableCollaborator[] }>(`/notas-fiscais/releases/collaborators${qs}`);
  return data.data ?? [];
}

export async function previewLiberacao(collaboratorId: string, referenceMonth: string): Promise<ReleasePreview> {
  const qs = new URLSearchParams({ collaboratorId, referenceMonth }).toString();
  const { data } = await api.get<ReleasePreview>(`/notas-fiscais/releases/preview?${qs}`);
  return data;
}

export async function liberarNotaFiscal(payload: { collaboratorId: string; referenceMonth: string }): Promise<NotaFiscal> {
  const { data } = await api.post<NotaFiscal>('/notas-fiscais/releases', payload);
  return data;
}

export async function cancelarLiberacao(id: string): Promise<void> {
  await api.delete(`/notas-fiscais/${id}/release`);
}

/** Envio e reenvio pelo colaborador: só número e PDF; o valor é o liberado. */
export async function enviarNotaFiscal(id: string, payload: { number: string; file: File }): Promise<NotaFiscal> {
  const formData = new FormData();
  formData.append('number', payload.number);
  formData.append('file', payload.file);
  const { data } = await api.post<NotaFiscal>(`/notas-fiscais/${id}/submit`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function aprovarNotaFiscal(id: string): Promise<NotaFiscal> {
  const { data } = await api.post<NotaFiscal>(`/notas-fiscais/${id}/approve`);
  return data;
}

export async function rejeitarNotaFiscal(id: string, reason: string): Promise<NotaFiscal> {
  const { data } = await api.post<NotaFiscal>(`/notas-fiscais/${id}/reject`, { reason });
  return data;
}

export async function faturarNotaFiscal(id: string): Promise<NotaFiscal> {
  const { data } = await api.post<NotaFiscal>(`/notas-fiscais/${id}/invoice`);
  return data;
}
